import { useCallback, useMemo, useRef } from "react";
import {
  hideWidget,
  moveWidget,
  resolveWidgetLayout,
  sameWidgetLayout,
  setWidgetCollapsed,
  showWidget,
  toStoredWidgetLayout,
  type StoredWidgetLayout,
  type WidgetLayout,
  type WidgetSurface,
} from "@heraion/shared";

import { useOptimisticSave } from "../useOptimisticSave";

/** Host seam: persist the layout (mobile patchProfile, web useProfileDoc) and
 *  resolve once the profile the host reads carries it. `undefined` clears the
 *  field, which means the surface's default layout. */
export type SaveWidgetLayout = (
  next: StoredWidgetLayout | undefined,
) => Promise<unknown>;

// HR-11, PG-57: the widget layout the athlete sees, applied at once. Home and
// Progress each pass their surface. The optimistic copy and the write queue
// are useOptimisticSave's.
export function useWidgetLayout<Id extends string>(
  surface: WidgetSurface<Id>,
  { stored, save }: { stored: StoredWidgetLayout | undefined; save: SaveWidgetLayout },
) {
  const persisted = useMemo(
    () => resolveWidgetLayout(surface, stored),
    [surface, stored],
  );
  const initial = useMemo(() => resolveWidgetLayout(surface), [surface]);
  const { draft, saveFailed, commit: queue } = useOptimisticSave<
    WidgetLayout<Id>,
    StoredWidgetLayout | undefined
  >(save);
  const layout = draft ?? persisted;

  const latest = useRef(layout);
  latest.current = layout;

  const commit = useCallback(
    (next: WidgetLayout<Id>, toStore: StoredWidgetLayout | undefined) => {
      if (sameWidgetLayout(next, latest.current)) return;
      latest.current = next;
      queue(next, toStore);
    },
    [queue],
  );

  const apply = useCallback(
    (op: (l: WidgetLayout<Id>) => WidgetLayout<Id>) => {
      const next = op(latest.current);
      commit(next, toStoredWidgetLayout(next));
    },
    [commit],
  );

  return {
    layout,
    saveFailed,
    move: (id: Id, toIndex: number) =>
      apply((l) => moveWidget(surface, l, id, toIndex)),
    hide: (id: Id) => apply((l) => hideWidget(surface, l, id)),
    show: (id: Id) => apply((l) => showWidget(l, id)),
    setCollapsed: (id: Id, collapsed: boolean) =>
      apply((l) => setWidgetCollapsed(surface, l, id, collapsed)),
    reset: () => commit(initial, undefined),
    isDefault: sameWidgetLayout(layout, initial),
  };
}
